import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  Landmark, TrendingUp, Building2, Wheat, FileText, ShieldCheck, BadgeCheck, ClipboardList,
  Check, Download, Loader2, Package, ArrowRight, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  Landmark, TrendingUp, Building2, Wheat, FileText, ShieldCheck, BadgeCheck, ClipboardList,
};

export default function Marketplace() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [installedCount, setInstalledCount] = useState(0);
  const [activeCat, setActiveCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/marketplace/packs").then((r) => {
      setPacks(r.data.packs);
      setCategories(r.data.categories);
      setInstalledCount(r.data.installed_count || 0);
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const install = async (pack, e) => {
    e?.stopPropagation();
    setInstalling(pack.id);
    try {
      const r = await api.post("/marketplace/install", { pack_id: pack.id });
      const counts = r.data.counts || {};
      const total = (counts.briefings || 0) + (counts.automations || 0) + (counts.knowledge || 0) + (counts.prompts || 0);
      toast.success(
        `${pack.name} installed · ${total} assets materialised`,
        {
          action: { label: "View pack →", onClick: () => navigate(`/marketplace/${pack.id}`) },
          duration: 6000,
        }
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Install failed");
    } finally {
      setInstalling(null);
    }
  };

  const filtered = useMemo(() => {
    if (activeCat === "all") return packs;
    if (activeCat === "installed") return packs.filter((p) => p.installed);
    return packs.filter((p) => p.category === activeCat);
  }, [packs, activeCat]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="marketplace-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-1">// Institutional AI Marketplace</div>
          <h1 className="font-display text-3xl text-white tracking-tight">Module Marketplace</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Curated institutional packs that extend Pandora Nexus with sector-specific templates, AI prompts, automation
            rules, and quick-actions. <span className="text-slate-300">When you install a pack, its assets land directly
            inside Engagement Hub, Automation, Knowledge and Copilot — and a pack page is created for one-click access.</span>
          </p>
        </div>
        {installedCount > 0 && (
          <button
            onClick={() => setActiveCat("installed")}
            data-testid="installed-jump"
            className="text-[11px] font-mono tracking-widest px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8 flex items-center gap-2"
          >
            <Sparkles className="w-3 h-3" /> {installedCount} INSTALLED · VIEW
          </button>
        )}
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2" data-testid="marketplace-categories">
        <CategoryChip label="All" active={activeCat === "all"} onClick={() => setActiveCat("all")} count={packs.length} />
        <CategoryChip
          label="Installed"
          active={activeCat === "installed"}
          onClick={() => setActiveCat("installed")}
          count={installedCount}
          gold
        />
        {categories.map((c) => (
          <CategoryChip
            key={c}
            label={c}
            active={activeCat === c}
            onClick={() => setActiveCat(c)}
            count={packs.filter((p) => p.category === c).length}
          />
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-slate-400 font-mono flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading marketplace…
        </div>
      ) : filtered.length === 0 ? (
        <div className="nexus-glass-panel p-10 text-center" data-testid="marketplace-empty">
          <Package className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg text-white">No packs in this view</h3>
          <p className="text-sm text-slate-400 mt-1">
            {activeCat === "installed" ? "Install a pack from any category to see it here." : "Try a different category."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="pack-grid">
          {filtered.map((pack, i) => {
            const Icon = ICONS[pack.icon] || Package;
            return (
              <motion.div
                key={pack.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => pack.installed && navigate(`/marketplace/${pack.id}`)}
                className={`nexus-glass-panel p-5 flex flex-col transition-all ${
                  pack.installed ? "border-[#D4AF37]/40 cursor-pointer hover:border-[#D4AF37]/70" : ""
                }`}
                data-testid={`pack-${pack.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="w-10 h-10 flex items-center justify-center"
                    style={{ background: pack.color + "20", border: `1px solid ${pack.color}40` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: pack.color }} strokeWidth={1.5} />
                  </div>
                  {pack.installed && (
                    <span className="font-mono text-[10px] text-[#D4AF37] tracking-widest flex items-center gap-1">
                      <Check className="w-3 h-3" /> INSTALLED
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg text-white">{pack.name}</h3>
                <p className="text-[12px] text-slate-400 mt-1 mb-3 leading-snug">{pack.tagline}</p>

                <div className="my-3">
                  <div className="label-eyebrow mb-2">// Includes</div>
                  <ul className="space-y-1">
                    {pack.includes.map((it, idx) => (
                      <li key={idx} className="text-[12px] text-slate-300 leading-snug flex gap-2">
                        <Check className="w-3 h-3 mt-1 shrink-0" style={{ color: pack.color }} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                  <div className="font-mono text-[9px] text-slate-500 tracking-widest line-clamp-1">
                    FOR: {(pack.audience || []).join(" · ")}
                  </div>
                  {pack.installed ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/marketplace/${pack.id}`); }}
                      data-testid={`open-${pack.id}`}
                      className="text-[11px] font-mono tracking-widest px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8 flex items-center gap-1.5"
                    >
                      OPEN <ArrowRight className="w-3 h-3" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => install(pack, e)}
                      disabled={installing === pack.id}
                      data-testid={`install-${pack.id}`}
                      className="text-[11px] font-mono tracking-widest px-3 py-1.5 transition-all disabled:opacity-50 flex items-center gap-1.5"
                      style={{ background: "var(--gold-500)", color: "#050B14" }}
                    >
                      {installing === pack.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Download className="w-3 h-3" strokeWidth={2.2} />
                      )}
                      INSTALL
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const CategoryChip = ({ label, active, onClick, count, gold }) => (
  <button
    onClick={onClick}
    data-testid={`cat-${label.toLowerCase()}`}
    className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest transition-all ${
      active
        ? gold ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#FCE588]" : "border-[#D4AF37]/60 bg-[#D4AF37]/8 text-white"
        : "border-white/10 text-slate-300 hover:border-white/25"
    }`}
  >
    {label.toUpperCase()}
    <span className={`ml-1.5 ${active && gold ? "text-[#D4AF37]" : "text-slate-500"}`}>{count}</span>
  </button>
);
